import { FC, useCallback } from "react";
import { useController } from "react-hook-form";
import { SharedProps } from "./SharedProps";
import { Input } from "./Input";
import {
  FieldBaseProps,
  SubmitGroup,
  ValidityGroup,
  useInputDisabled,
  useInputReadonly,
  Validation,
} from "capitalrx-components-2.0/lib/forms";
import { Spinner } from "capitalrx-components-2.0/lib/general";

/**
 * The `Field` component properties.
 */

export interface FieldProps extends FieldBaseProps<string | null>, SharedProps {
  /**
   * The minimum number of characters allowed.
   */
  readonly minLength?: number;

  /**
   * sets the value of the input
   */
  // eslint-disable-next-line
  readonly form: any;

  /**
   * When true, indicates that the query is currently fetching, but might have data from an earlier request.
   */
  readonly isLoading: boolean;

  /**
   * The function that will be fetching the data. used to trigger the api call
   */
  readonly fetchData: (data: string) => void;

  /**
   * Custom menu item click
   */
  // eslint-disable-next-line
  readonly onCustomMenuItemClick?: (data: any) => void;
}

/**
 * A React Hook Form compatible Textbox Field component. For more information, see the documentation for
 * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
 */
export const Field: FC<FieldProps> = (props: FieldProps) => {
  const {
    name,
    required = false,
    minLength,
    maxLength,
    defaultValue = null,
    validator,
    searchResults,
    isLoading,
    fetchData,
    form,
    onCustomMenuItemClick,
    ...restProps
  } = props;

  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);

  const rules = {
    ...Validation.required(required),
    ...Validation.length(minLength, maxLength),
    validate: validator,
  };

  const { field, fieldState, formState } = useController({
    name,
    defaultValue,
    rules: !disabled && !readOnly ? rules : undefined,
  });

  const { ref, value, onChange, onBlur } = field;
  const { invalid, error } = fieldState;
  const { isSubmitting } = formState;

  const onMenuItemClick = useCallback(
    // eslint-disable-next-line
    (data: any) => {
      const setValue = form?.current ? form?.current?.setValue : form?.setValue;

      if (data && setValue) {
        setValue && setValue(name, data.value);
      }

      onCustomMenuItemClick && onCustomMenuItemClick(data.data);
    },
    [form],
  );

  return (
    <SubmitGroup submitting={isSubmitting}>
      <ValidityGroup invalid={invalid} error={error?.message}>
        <Input
          {...restProps}
          suffix={isLoading && <Spinner />}
          ref={ref}
          value={value}
          type="text"
          required={required}
          maxLength={maxLength}
          defaultValue={defaultValue}
          onChange={onChange}
          onFocusExit={onBlur}
          searchResults={searchResults}
          onMenuItemClick={onMenuItemClick}
          fetchData={fetchData}
        />
      </ValidityGroup>
    </SubmitGroup>
  );
};
